import { callManualSearchApiWithCallback } from "@/api/ChatApi";

/**
 * マニュアル検索画面
 *
 * d-prompt-manager の manual ドキュメント RAG 検索（frontend-admin の
 * 日誌AIアシスタントと同等）を呼び出し、質問に対する回答と参照元マニュアルを表示する。
 */
export default {
  name: "ManualSearch",
  data() {
    return {
      inputText: "",
      isStreaming: false,
      messages: [],
      enlargedImage: null,
    };
  },
  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messageArea;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    enlargeImage(url) {
      this.enlargedImage = url;
    },
    onEnterKey(event) {
      // IME変換中（日本語入力の変換確定）のEnterでは送信しない
      if (event.isComposing || event.keyCode === 229) {
        return;
      }
      event.preventDefault();
      this.sendMessage();
    },
    sendMessage() {
      const question = this.inputText.trim();
      if (!question || this.isStreaming) {
        return;
      }

      // ユーザーメッセージ + アシスタントのプレースホルダを追加
      this.messages.push({ role: "user", content: question });
      this.messages.push({ role: "assistant", content: "", ragItems: [] });
      const assistantIndex = this.messages.length - 1;

      this.inputText = "";
      this.isStreaming = true;
      this.scrollToBottom();

      callManualSearchApiWithCallback(
        question,
        // チャンクごとのコールバック
        (chunk) => {
          this.handleChunk(chunk, assistantIndex);
        },
        // 完了時のコールバック
        () => {
          this.isStreaming = false;
          const msg = this.messages[assistantIndex];
          if (!msg.content && (!msg.ragItems || msg.ragItems.length === 0)) {
            this.updateAssistant(assistantIndex, {
              content: "該当する回答が見つかりませんでした。",
            });
          }
          this.scrollToBottom();
        },
        // エラー時のコールバック
        (error) => {
          console.error("マニュアル検索でエラーが発生しました:", error);
          this.isStreaming = false;
          // 利用可能な理由があればそれを出す。権限を落とされている場合など、
          // 「エラーが発生しました」だけでは利用者が原因に辿り着けない。
          const reason = (error && error.userMessage) || "";
          this.updateAssistant(assistantIndex, {
            content: reason || "エラーが発生しました。もう一度お試しください。",
          });
          this.$bvToast.toast(
            reason || "マニュアル検索中にエラーが発生しました。",
            {
              title: "エラー",
              variant: "danger",
              solid: true,
            }
          );
        }
      );
    },
    handleChunk(chunk, assistantIndex) {
      let data = chunk;
      try {
        data = typeof chunk === "string" ? JSON.parse(chunk) : chunk;
      } catch (e) {
        if (typeof chunk === "string") {
          this.appendAssistantToken(assistantIndex, chunk);
          this.scrollToBottom();
        }
        return;
      }

      const result = data && data.result ? data.result : null;
      if (!result) {
        return;
      }

      // 最初のメタデータチャンクは無視
      if (result.chatMetadata) {
        return;
      }

      // エラーの場合
      if (result.errorCode || result.errorMessage) {
        this.appendAssistantToken(
          assistantIndex,
          result.errorMessage || "エラーが発生しました。"
        );
        this.scrollToBottom();
        return;
      }

      // 参照元（マニュアル検索結果）
      if (
        result.ragRetrievalResult &&
        Array.isArray(result.ragRetrievalResult.items)
      ) {
        this.updateAssistant(assistantIndex, {
          ragItems: result.ragRetrievalResult.items,
        });
        this.scrollToBottom();
      }

      // ストリーミングテキスト（思考トークンは除外）
      if (result.token && result.tokenType !== "THINKING") {
        this.appendAssistantToken(assistantIndex, result.token);
        this.scrollToBottom();
      }
    },
    appendAssistantToken(assistantIndex, token) {
      const current = this.messages[assistantIndex];
      this.updateAssistant(assistantIndex, {
        content: (current.content || "") + token,
      });
    },
    // アシスタントメッセージを部分更新する（リアクティブに反映するため $set を使用）
    updateAssistant(assistantIndex, patch) {
      const current = this.messages[assistantIndex];
      this.$set(this.messages, assistantIndex, {
        ...current,
        role: "assistant",
        ...patch,
      });
    },
  },
};
